// Package imports:
import React, { ChangeEvent, ForwardedRef, forwardRef, memo, MouseEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";

// Define the base props that extend HTML input attributes
type CheckProps = React.InputHTMLAttributes<HTMLInputElement>;

// Interface for additional custom props specific to our checkbox component
interface IProps extends CheckProps {
    label?: string | JSX.Element;
    labelComponent?: JSX.Element;
    hasError?: boolean;
    errorMessage?: string;
    inputSize?: "sm" | "lg";
}

const Checkbox = forwardRef(function Checkbox(
    {
        id,
        label,
        className = "",
        value,
        onChange = () => { },
        labelComponent,
        name,
        hasError,
        errorMessage,
        inputSize = "lg",
        ...props
    }: IProps,
    ref: ForwardedRef<any>
) {
    const handleOnMouseDown = (event: MouseEvent<HTMLInputElement>) => {
        // setOnchange(!value);
        if (props.disabled) return;
        const newCheckedState = !(value === "true");
        const syntheticChangeEvent = {
            ...event,
            target: {
                ...event.currentTarget,
                checked: newCheckedState,
                value: newCheckedState.toString(),
                type: "checkbox",
                name: name || "",
            },
        } as ChangeEvent<HTMLInputElement>;
        onChange(syntheticChangeEvent);

        // Prevent the default action to avoid double toggling
        event.preventDefault();
    };

    return (
        <div
            className={cx("KCL_checkbox", className, inputSize, {
                "is-disabled": props.disabled,
                "has-error": hasError,
                checked: value === "true",
            })}
        >
            <span onMouseDown={handleOnMouseDown} className="checkbox__wrapper">
                <input
                    ref={ref}
                    id={id}
                    type="checkbox"
                    name={name}
                    checked={value === "true"}
                    onMouseDown={handleOnMouseDown}
                    // No-op to suppress React warning about controlled component
                    onChange={() => { }}
                    {...props}
                />
                <label htmlFor={id} className="checkbox__label">
                    <span className="checkbox__icon">
                        <i>
                            <FontAwesomeIcon icon={faCheck} />
                        </i>
                    </span>
                    {/* Render the label text if provided */}
                    {label && <span>{label}</span>}
                    {/* Render the custom label component if provided */}
                    {labelComponent && <span>{labelComponent}</span>}
                </label>
            </span>

            {/* Show error message only if there's an error and a message is provided */}
            {hasError && errorMessage && <p className="input-error-message">{errorMessage}</p>}
        </div>
    );
});

export default memo(Checkbox);
